<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title mb-3"> CPA認証ID一覧</v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-card v-permission.config>
          <v-card-text class="pa-10">
            <validation-observer v-slot="{ handleSubmit }" as="div">
              <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
                <v-row>
                  <v-col>
                    <app-badge
                      item="作成ファイル種別"
                      :isRequired="false"
                      :isInfo="false"
                    />
                    <v-radio-group inline v-model="form.fileType" hide-details>
                      <v-radio label="アカウント一覧" value="ACCOUNT_LIST" />
                      <v-radio
                        label="アカウンティングログ"
                        value="ACCOUNTING_LOG"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>

                <v-row class="mb-3">
                  <v-col cols="8">
                    <app-badge item="ドメイン" :isInfo="false" />
                    <validation-provider
                      name="domain"
                      label="ドメイン"
                      v-model="form.domain"
                      :rules="{ required: true }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select-search
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                        :items="domainList"
                        item-title="domain"
                        item-value="domain"
                        return-object
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <div v-if="form.fileType === 'ACCOUNTING_LOG'">
                      <app-badge
                        item="期間"
                        :isRequired="false"
                        msg="・終了日時は開始日時より後を指定
                      ・時刻は00:00:00～23:59:59の範囲で指定し、時分秒はそれぞれ2桁で入力
                      ・秒は省略可"
                      />
                      <div class="mt-2 ml-3">
                        <!-- 開始日時 -->
                        <app-badge item="開始日時" :isInfo="false" />
                        <div class="d-flex ga-3">
                          <div>
                            <validation-provider
                              name="dateFrom"
                              label="開始日付"
                              v-model="form.dateFrom"
                              :rules="{ required: true }"
                              v-slot="{ componentField, errorMessage, meta }"
                            >
                              <app-form-input
                                v-bind="componentField"
                                :max="maxSendDate"
                                :field-meta="meta"
                                :error-messages="errorMessage"
                                :placeholder="
                                  $filter.message('placeholder_date')
                                "
                                type="text"
                                onfocus="this.type='date'"
                                onblur="this.type='text'"
                                class="input-period-date"
                                @keydown.prevent
                              >
                              </app-form-input>
                            </validation-provider>
                          </div>
                          <div>
                            <validation-provider
                              label="開始時間"
                              name="timeFrom"
                              v-model="form.timeFrom"
                              :rules="{
                                required: true,
                                is_time_custom: true,
                                is_before_current_date: { date: form.dateFrom },
                              }"
                              v-slot="{ componentField, errorMessage, meta }"
                            >
                              <app-form-input
                                v-bind="componentField"
                                :field-meta="meta"
                                :error-messages="errorMessage"
                                :placeholder="
                                  $filter.message('placeholder_moment_custom')
                                "
                                class="input-period-time"
                              />
                            </validation-provider>
                          </div>
                        </div>
                        <!-- 終了日時 -->
                        <app-badge item="終了日時" :isInfo="false" />
                        <div class="d-flex ga-3">
                          <div>
                            <validation-provider
                              name="dateTo"
                              label="終了日付"
                              v-model="form.dateTo"
                              :rules="{
                                required: true,
                                is_datetime_after: {
                                  from: dateTimeFrom,
                                  to: dateTimeTo,
                                },
                                is_between_start_date_and_end_date: {
                                  from: form.dateFrom,
                                  days: 150,
                                },
                              }"
                              v-slot="{ componentField, errorMessage, meta }"
                            >
                              <app-form-input
                                v-bind="componentField"
                                :max="maxSendDate"
                                :field-meta="meta"
                                :error-messages="errorMessage"
                                :placeholder="
                                  $filter.message('placeholder_date')
                                "
                                type="text"
                                onfocus="this.type='date'"
                                onblur="this.type='text'"
                                class="input-period-date"
                                @keydown.prevent
                              >
                              </app-form-input>
                            </validation-provider>
                          </div>
                          <div>
                            <validation-provider
                              name="timeTo"
                              label="終了時間"
                              v-model="form.timeTo"
                              :rules="{
                                required: true,
                                is_time_custom: true,
                                is_datetime_after: {
                                  from: dateTimeFrom,
                                  to: dateTimeTo,
                                },
                                is_before_current_date: { date: form.dateTo },
                              }"
                              v-slot="{ componentField, errorMessage, meta }"
                            >
                              <app-form-input
                                v-bind="componentField"
                                :field-meta="meta"
                                :error-messages="errorMessage"
                                :placeholder="
                                  $filter.message('placeholder_moment_custom')
                                "
                                class="input-period-time"
                              />
                            </validation-provider>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <div class="d-flex justify-end">
                  <v-btn type="submit" color="primary">ファイル作成</v-btn>
                </div>
              </v-form>
            </validation-observer>
          </v-card-text>
        </v-card>

        <v-row class="mt-6" no-gutters>
          <v-col class="pr-4">
            <app-search :search="searchOption" v-model="cpaId.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn color="primary" @click="deleteFiles">削除</v-btn>
          </div>
        </v-row>

        <v-alert v-if="alertMsg" class="mt-4 mb-4" :text="alertMsg"></v-alert>

        <div>
          <div class="d-flex flex-row justify-end mb-2">
            <v-btn color="primary" @click="getCpaIdFileData">一覧更新</v-btn>
          </div>
          <app-table
            primary-key="cpaIdInformationSeq"
            :fields="cpaId.fields"
            :items="cpaId.items"
            :selectable="true"
            v-model="cpaId.selected"
            :hover="true"
            @click:row="downloadIdFile"
            :filter="cpaId.filter"
            :filterOption="cpaId.filter_option"
            v-model:table-sort-by="cpaId.sortBy"
            :tr-class="cpaIdRowClass"
            sticky-header="50vh"
          >
            <!-- チェックボックス -->
            <template #cell(_selected)="data">
              <div v-if="data.item.status !== 'EXECUTION'">
                <v-checkbox-btn
                  :value="data.item"
                  v-model="cpaId.selected"
                  @click.stop
                ></v-checkbox-btn>
              </div>
            </template>

            <!-- ファイル種別 -->
            <template #cell(fileType)="data">
              <span>{{ $filter.enumTo(data.value, "cpaIdFileType") }}</span>
            </template>

            <!-- ファイルサイズ -->
            <template #cell(fileSize)="data">
              <div class="text-right">
                {{ convertFileSize(data.value) }}
              </div>
            </template>

            <!-- ファイル作成期間 -->
            <template #cell(fileCreationPeriod)="data">
              <span v-if="data.value">
                {{
                  $filter.datetime(data.item.fileCreationPeriod.startDateTime, {
                    s: true,
                  })
                }}
                ~
                {{
                  $filter.datetime(data.item.fileCreationPeriod.endDateTime, {
                    s: true,
                  })
                }}
              </span>
              <span v-else>-</span>
            </template>

            <!-- 作成実行日 -->
            <template #cell(executeDateTime)="data">
              <span>{{ $filter.datetime(data.value, { s: true }) }}</span>
            </template>

            <!-- ステータス -->
            <template #cell(status)="data">
              <span>{{ $filter.enumTo(data.value, "cpaIdFileStatus") }}</span>
            </template>

            <!-- 作成状況 -->
            <template #cell(progressRate)="data">
              <div class="text-right">
                {{
                  data.item.fileType === "ACCOUNTING_LOG" &&
                  data.item.status === "EXECUTION"
                    ? data.value + "%"
                    : "-"
                }}
              </div>
            </template>

            <!-- 作成権限 -->
            <template #cell(authorityType)="data">
              <span>
                {{ $filter.enumTo(data.value, "cpaIdAuthorityType") }}
              </span>
            </template>
          </app-table>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { ApiForbiddenError } from "@/error/errors";
import type { CpaDomain, CpaIdFile, FileType } from "@/apis/CpaIdApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import ApiNotFoundModal from "@/modals/error/ApiNotFoundModal.vue";
import CpaIdFileDownloadConfirm from "@/modals/cpaAuthId/CpaIdFileDownloadConfirm.vue";
import CpaAccessErrorModal from "@/modals/cpaAuthId/CpaAccessErrorModal.vue";

export default defineComponent({
  name: "CpaAuthIdList",
  data() {
    return {
      isLoaded: false,
      alertMsg: null as string | null,
      form: {
        fileType: "ACCOUNT_LIST" as FileType,
        domain: {} as CpaDomain | null,
        dateFrom: "",
        timeFrom: "",
        dateTo: "",
        timeTo: "",
      },
      domainList: [] as CpaDomain[],
      cpaId: {
        sortBy: {
          key: "executeDateTime",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "domain",
            title: "ドメイン",
            width: "200px",
          },
          {
            key: "fileType",
            title: "ファイル種別",
            width: "200px",
          },
          {
            key: "fileSize",
            title: "ファイルサイズ",
            width: "160px",
          },
          {
            key: "fileCreationPeriod",
            title: "ファイル作成期間",
            width: "300px",
          },
          {
            key: "executeDateTime",
            title: "作成実行日",
            width: "200px",
          },
          {
            key: "status",
            title: "ステータス",
            width: "160px",
          },
          {
            key: "progressRate",
            title: "作成状況",
            width: "160px",
          },
          {
            key: "authorityType",
            title: "作成権限",
            width: "160px",
          },
        ] as AppTableHeader<CpaIdFile>[],
        items: [] as CpaIdFile[],
        selected: [] as CpaIdFile[],
        filter: {} as Record<string, unknown>,
        filter_option: {
          domain: "equal",
          fileType: "equal",
          status: "equal",
        } as FilterOption,
      },
      domainOptions: [{ value: "", text: "" }],
    };
  },

  async mounted() {
    this.form.domain = null;
    await this.load();
  },

  computed: {
    /** 検索コンポーネントのオプション */
    searchOption(): SearchOption {
      return {
        domain: {
          type: "select",
          label: "ドメイン",
          items: this.domainList.map((e) => ({
            value: e.domain,
            text: e.domain,
          })),
        },
        fileType: {
          type: "select",
          label: "ファイル種別",
          items: [
            { value: "ACCOUNT_LIST", text: "アカウント一覧" },
            { value: "ACCOUNTING_LOG", text: "アカウンティングログ" },
          ],
        },
        status: {
          type: "select",
          label: "ステータス",
          items: [
            { value: "SUCCESS", text: "成功" },
            { value: "FAILURE", text: "失敗" },
            { value: "EXECUTION", text: "作成中" },
            { value: "NODATA", text: "該当データ無し" },
          ],
        },
      };
    },
    /** fromの日と時間を結合した文字列 */
    dateTimeFrom(): string | null {
      if (!this.form.dateFrom) return null;
      if (!this.form.timeFrom) {
        return `${this.form.dateFrom}T00:00:00`;
      } else {
        return `${this.form.dateFrom}T${this.form.timeFrom}`;
      }
    },
    /** toの日と時間を結合した文字列 */
    dateTimeTo(): string | null {
      if (!this.form.dateTo) return null;
      if (!this.form.timeTo) {
        return `${this.form.dateTo}T23:59:59`;
      } else {
        return `${this.form.dateTo}T${this.form.timeTo}`;
      }
    },
    /** 現在の日付以前が選択可能 */
    maxSendDate(): string {
      return this.$moment().format("YYYY-MM-DD");
    },
  },

  methods: {
    async load() {
      /** ドメイン・ファイル一覧を取得 */
      this.domainList = (await this.$api.cpaIdApi.getCpaDomains()).domainList;
      await this.getCpaIdFileData();
      // 作成権限は代行権限のユーザのみ表示
      if (!this.$service.permission.isDelegateUser()) {
        this.cpaId.fields.splice(7, 1);
      }
      this.isLoaded = true;
    },

    async getCpaIdFileData() {
      this.alertMsg = null;
      try {
        this.cpaId.items = (
          await this.$api.cpaIdApi.getCpaAccounts()
        ).cpaIdFileList;
      } catch (e) {
        if (e instanceof ApiForbiddenError) {
          // CPA設定情報が存在しない場合はエラーモーダルを表示
          await this.$modal.show(CpaAccessErrorModal);
          return;
        }
      }
    },

    /** ファイル作成 */
    async submit() {
      this.alertMsg = null;
      await this.$confirm(`指定されたファイルを作成します。よろしいですか？`);
      await this.$api.cpaIdApi.createCpaContract(
        this.form.domain!.cpaContractSeq,
        {
          fileType: this.form.fileType,
          startDateTime:
            this.form.fileType === "ACCOUNTING_LOG"
              ? this.$moment(
                  this.form.dateFrom + " " + this.form.timeFrom
                ).format("YYYY-MM-DDTHH:mm:ss+09:00")
              : null,
          endDateTime:
            this.form.fileType === "ACCOUNTING_LOG"
              ? this.$moment(this.form.dateTo + " " + this.form.timeTo).format(
                  "YYYY-MM-DDTHH:mm:ss+09:00"
                )
              : null,
        }
      );
      // 一覧を更新
      await this.getCpaIdFileData();
    },

    /** ファイルダウンロード確認 */
    async downloadIdFile(entity: CpaIdFile) {
      // 成功の場合のみダウンロード可能
      if (entity.status !== "SUCCESS") {
        return;
      }
      this.alertMsg = null;
      // ダウンロード前に一覧を取得して対象ファイルの存在チェックを行う
      const isFileExist = (
        await this.$api.cpaIdApi.getCpaAccounts()
      ).cpaIdFileList.some(
        (v: CpaIdFile) => v.cpaIdInformationSeq === entity.cpaIdInformationSeq
      );

      // ファイルが存在すれば確認ダイアログを表示 はいを押下した場合ダウンロードAPIを呼び出す
      if (isFileExist) {
        await this.$modal.show(CpaIdFileDownloadConfirm, {
          cpaIdInformationSeq: entity.cpaIdInformationSeq,
          authorityType: entity.authorityType,
        });
      } else {
        // ファイルが存在しない場合はエラーモーダルを表示
        await this.$modal.show(ApiNotFoundModal);
      }
    },

    convertFileSize(size: number): string {
      // APIからMB単位で返却された値が1未満の場合、固定値「1MB未満」を返却する
      if (size < 1) {
        return "1MB未満";
      }
      const gb = 1000;
      /** APIからMB単位で返却された値が1000MB超過する場合は、GBに変換する */
      let target = size;
      let unit = "MB";

      if (size >= gb) {
        target = gb;
        unit = "GB";
      }
      return `${size >= gb ? (size / target).toFixed(1) : size}${unit}`;
    },
    /**
     * ファイル削除
     */
    async deleteFiles() {
      if (this.cpaId.selected.length === 0) {
        this.alertMsg = this.$filter.message("no_select_data");
        return;
      }
      this.alertMsg = null;

      // 法人権限で作成されたレコードが存在するかチェック;
      const isAuthCustomer = this.cpaId.selected.some(
        (e: CpaIdFile) => e.authorityType === "CUSTOMER"
      );
      await this.$modal.show(CpaIdFileDownloadConfirm, {
        authorityType: isAuthCustomer ? "CUSTOMER" : "OPERATOR",
      });

      // 存在チェック用に現時点の一覧情報を取得
      const presentPoint = (await this.$api.cpaIdApi.getCpaAccounts())
        .cpaIdFileList;

      for (const e of this.cpaId.selected) {
        // 現時点でファイルが存在するかチェック
        const isFileExist = presentPoint.some(
          (v: CpaIdFile) => v.cpaIdInformationSeq === e.cpaIdInformationSeq
        );

        // 存在する場合、削除処理を実施
        if (isFileExist) {
          await this.$api.cpaIdApi.deleteCpaAccount(e.cpaIdInformationSeq);
        }
      }
      await this.getCpaIdFileData();
    },
    /** CpaIdテーブルのtrクラス
     * 成功時のみダウンロードできる
     **/
    cpaIdRowClass(item: CpaIdFile): string[] | undefined {
      switch (item.status) {
        case "SUCCESS":
          return ["clickable"];
        default:
          return undefined;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.input-period-date {
  width: 180px;
}

.input-period-time {
  width: 200px;
}
</style>
